/**
 * ProductDetailPage — live product detail (Blinkit-style): image, price,
 * description, details, and a "You might also like" rail. Its own minimal
 * header + sticky add-to-cart bar. Standalone route (no orange navbar).
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Share2, ChevronRight, ShoppingCart, Package } from 'lucide-react';
import { formatCurrency, calculateDiscountPercent, productSizeLabel } from '@rocket07/shared';
import { ProductCard } from '@/components/product/ProductCard';
import { AddButton } from '@/components/product/AddButton';
import { AiImageNote } from '@/components/product/AiImageNote';
import { Button, EmptyState, Spinner, ErrorState } from '@/components/ui';
import { useCart } from '@/context/CartContext';
import { useCollege } from '@/context/CollegeContext';
import { useProduct, useProducts } from '@/hooks/useStorefront';
import { productImage } from '@/utils/productImage';
import { ROUTES } from '@/constants/routes';

export function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { collegeId } = useCollege();
  const { itemCount, addItem, getQuantity } = useCart();

  const { data: product, isLoading, isError, error, refetch } = useProduct(slug, collegeId);
  const { data: relatedData } = useProducts({ collegeId, limit: 6 });

  // Tracks WHICH url failed, not just that one did — a boolean would latch and
  // keep hiding a replacement image an admin uploaded during the same session.
  const [failedSrc, setFailedSrc] = useState(null);
  const candidate = product?.image || productImage(product?.name);
  const imageSrc = candidate && candidate !== failedSrc ? candidate : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-canvas">
        <MinimalHeader onBack={() => navigate(-1)} />
        <div className="grid min-h-[60vh] place-items-center">
          <Spinner size="lg" className="text-primary-600" />
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="min-h-dvh bg-canvas">
        <MinimalHeader onBack={() => navigate(ROUTES.home)} />
        {isError ? (
          <ErrorState error={error} onRetry={refetch} className="min-h-[60vh]" />
        ) : (
          <EmptyState
            icon={Package}
            title="Product not found"
            description="This product may have been removed or isn't available at your campus."
            action={
              <Link to={ROUTES.home}>
                <Button>Back to home</Button>
              </Link>
            }
            className="min-h-[60vh]"
          />
        )}
      </div>
    );
  }

  const discount = calculateDiscountPercent(product.mrp, product.price);
  const related = (relatedData?.items ?? []).filter((p) => p.id !== product.id).slice(0, 6);
  const inCart = getQuantity(product.id);
  const sizeLabel = productSizeLabel(product);

  // Stock COUNT is admin-only and never sent to customers — we only use the
  // derived `inStock` flag to gate buying, and show availability, not numbers.
  const details = [
    ['Brand', product.brand],
    ['Category', product.category?.name],
    ['Pack size', sizeLabel],
    ['Availability', product.inStock ? 'In stock' : 'Out of stock'],
  ].filter(([, v]) => v);

  return (
    <div className="min-h-dvh bg-canvas pb-24">
      <MinimalHeader onBack={() => navigate(-1)} />

      <div className="mx-auto max-w-3xl px-4">
        {/* Image */}
        <div className="mt-2 grid aspect-square place-items-center overflow-hidden rounded-2xl bg-tile">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.name}
              onError={() => setFailedSrc(imageSrc)}
              className="h-full w-full object-contain p-4"
            />
          ) : (
            <span className="text-[7rem]" aria-hidden="true">
              {product.emoji}
            </span>
          )}
        </div>

        {/*
          The detail page is where the decision is actually made, and there is
          room for the part that matters: the picture is illustrative, so the
          item in the bag may not look exactly like it. Only shown when a real
          photo rendered — never under the emoji fallback.
        */}
        {imageSrc && (
          <AiImageNote
            label="AI-generated image — the actual product may look different"
            className="mt-2"
          />
        )}

        {/* Name + price */}
        <div className="mt-4">
          {sizeLabel && (
            <span className="inline-block rounded-md bg-tile px-2 py-0.5 text-xs font-semibold text-ink">
              {sizeLabel}
            </span>
          )}
          <h1 className="mt-2 text-lg font-bold leading-snug text-ink">{product.name}</h1>
          {product.brand && <p className="mt-0.5 text-sm text-muted">{product.brand}</p>}

          <div className="mt-2 flex items-center gap-2">
            {discount > 0 && <span className="text-sm font-bold text-success">↓{discount}%</span>}
            {discount > 0 && (
              <span className="text-sm text-muted line-through">{formatCurrency(product.mrp)}</span>
            )}
            <span className="text-xl font-extrabold text-ink">{formatCurrency(product.price)}</span>
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <section className="mt-6">
            <h2 className="text-base font-bold text-ink">Product Description</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink/80">{product.description}</p>
          </section>
        )}

        {/* Details */}
        <section className="mt-5">
          <h2 className="text-base font-bold text-ink">Details</h2>
          <dl className="mt-2 divide-y divide-line rounded-xl border border-line">
            {details.map(([label, value]) => (
              <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <dt className="text-muted">{label}</dt>
                <dd className="font-medium text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* You might also like */}
        {related.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-3 text-lg font-extrabold tracking-tight text-ink">
              You might also like
            </h2>
            <div className="grid grid-cols-3 gap-x-2.5 gap-y-5 sm:grid-cols-4">
              {related.map((item) => (
                <ProductCard key={item.id} product={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky bottom action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white px-4 py-3 shadow-bar">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <Link
            to={ROUTES.cart}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-primary-500 bg-primary-50/40 py-3 text-sm font-bold text-primary-700"
          >
            <ShoppingCart size={18} />
            <span className="leading-tight">
              Go to cart
              <span className="block text-[11px] font-medium text-muted">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
            </span>
          </Link>

          {product.inStock === false ? (
            <div className="flex flex-1 items-center justify-center rounded-xl bg-line py-3.5 text-sm font-bold text-muted">
              Out of stock
            </div>
          ) : inCart === 0 ? (
            <button
              type="button"
              onClick={() => addItem(product)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary-500 py-3.5 text-sm font-bold text-ink active:scale-[0.99]"
            >
              Add to cart <ChevronRight size={18} />
            </button>
          ) : (
            <div className="flex-1">
              <AddButton product={product} size="lg" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MinimalHeader({ onBack }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 flex items-center justify-between bg-white px-4 py-3 shadow-nav">
      <button
        type="button"
        onClick={onBack}
        aria-label="Go back"
        className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink hover:bg-canvas"
      >
        <ArrowLeft size={20} />
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(ROUTES.search)}
          aria-label="Search"
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink hover:bg-canvas"
        >
          <Search size={19} />
        </button>
        <button
          type="button"
          aria-label="Share"
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink hover:bg-canvas"
        >
          <Share2 size={18} />
        </button>
      </div>
    </header>
  );
}

export default ProductDetailPage;
